import DeleteIcon from '@material-design-icons/svg/filled/delete.svg?react';
import InsertDriveFileIcon from '@material-design-icons/svg/filled/insert_drive_file.svg?react';
import type { Release } from '@src/dto/Release';
import { getStatusDisplay } from '../../utils/releaseStatusConfig';
import { formatDate, formatFileSize } from '../../utils/stringHelpers';
import { DataTable } from '../common/DataTable';

interface DashboardReleasesTableProps {
  releases: Release[];
  currentPage: number;
  showActionsColumn: boolean;
  onPageChange: (page: number) => void;
  onOpenRelease: (release: Release) => void;
  onRequestDeleteRelease: (release: Release) => void;
}

const releasesPerPage = 10;

export function DashboardReleasesTable({
  releases,
  currentPage,
  showActionsColumn,
  onPageChange,
  onOpenRelease,
  onRequestDeleteRelease,
}: DashboardReleasesTableProps) {
  const totalPages = Math.ceil(releases.length / releasesPerPage);
  const startIndex = (currentPage - 1) * releasesPerPage;
  const endIndex = startIndex + releasesPerPage;
  const currentReleases = releases.slice(startIndex, endIndex);
  const versionColumnWidth = showActionsColumn ? 'w-[19%]' : 'w-[20%]';
  const uploadDateColumnWidth = showActionsColumn ? 'w-[13%]' : 'w-[13%]';
  const statusColumnWidth = showActionsColumn ? 'w-[14%]' : 'w-[14%]';
  const fileNameColumnWidth = showActionsColumn ? 'w-[25%]' : 'w-[28%]';
  const fileSizeColumnWidth = showActionsColumn ? 'w-[8%]' : 'w-[9%]';
  const uploadedByColumnWidth = showActionsColumn ? 'w-[13%]' : 'w-[16%]';

  const releaseColumns = [
    {
      key: 'version',
      header: 'Version',
      headerClassName: `text-left text-neutral-400 text-xs font-normal px-4 py-2 bg-[#161717] whitespace-nowrap ${versionColumnWidth}`,
      className: `px-4 py-2 ${versionColumnWidth} align-middle`,
      accessor: (release: Release) => {
        const isLegacy = release.source === 'Hubspot';
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-1 min-w-0">
              <span className="text-white font-medium text-sm group-hover:text-teal-400 transition-colors">
                {release.versionName || 'Unknown'}
              </span>
              {release.versionCode && (
                <span className="text-neutral-500 text-xs group-hover:text-neutral-300 transition-colors">
                  ({release.versionCode})
                </span>
              )}
            </div>
            {isLegacy && (
              <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs font-normal bg-neutral-800/50 text-neutral-500">
                via cli
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'uploadDate',
      header: 'Upload Date',
      headerClassName: `text-left text-neutral-400 text-xs font-normal px-4 py-2 bg-[#161717] whitespace-nowrap ${uploadDateColumnWidth}`,
      className: `px-4 py-2 ${uploadDateColumnWidth} align-middle`,
      accessor: (release: Release) => (
        <span className="text-white text-sm group-hover:text-neutral-200 transition-colors">
          {formatDate(release.created)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      headerClassName: `text-left text-neutral-400 text-xs font-normal px-4 py-2 bg-[#161717] whitespace-nowrap ${statusColumnWidth}`,
      className: `px-4 py-2 ${statusColumnWidth} align-middle`,
      accessor: (release: Release) => {
        const statusDisplay = getStatusDisplay(
          release.status,
          release.submissionStatus,
          {
            isLive: release.isLive,
            liveVersionComparison: release.liveVersionComparison,
            hubspotTicketId: release.hubspotTicketId,
            hubspotTicketClosedDate: release.hubspotTicketClosedDate,
            nftMintAddress: release.nftMintAddress,
            isSupersededByNewerSubmission:
              release.isSupersededByNewerSubmission,
          },
        );
        const StatusIcon = statusDisplay.icon;

        return (
          <div>
            <div className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <StatusIcon
                className={`shrink-0 pointer-events-none ${statusDisplay.iconClassName}`}
              />
              <span
                className={`text-sm font-medium ${statusDisplay.textColorClassName}`}
              >
                {statusDisplay.label}
              </span>
            </div>
            {(release.processingError || release.rejectionReason) && (
              <div
                className="text-xs text-red-400 mt-0.5 truncate"
                title={
                  release.processingError ||
                  release.rejectionReason ||
                  undefined
                }
              >
                {release.processingError || release.rejectionReason}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'fileName',
      header: 'File Name',
      headerClassName: `text-left text-neutral-400 text-xs font-normal px-4 py-2 bg-[#161717] whitespace-nowrap ${fileNameColumnWidth}`,
      className: `px-4 py-2 ${fileNameColumnWidth} align-middle`,
      accessor: (release: Release) => (
        <div className="flex items-center gap-2">
          <InsertDriveFileIcon className="size-4 fill-neutral-400 group-hover:fill-neutral-300 transition-colors flex-shrink-0" />
          <span
            className="text-white text-sm group-hover:text-neutral-200 transition-colors truncate"
            title={release.releaseFileName}
          >
            {release.releaseFileName}
          </span>
        </div>
      ),
    },
    {
      key: 'fileSize',
      header: 'File Size',
      headerClassName: `text-left text-neutral-400 text-xs font-normal px-4 py-2 bg-[#161717] whitespace-nowrap ${fileSizeColumnWidth}`,
      className: `px-4 py-2 ${fileSizeColumnWidth} align-middle`,
      accessor: (release: Release) => (
        <span className="text-white text-sm group-hover:text-neutral-200 transition-colors whitespace-nowrap">
          {formatFileSize(release.releaseFileSize)}
        </span>
      ),
    },
    {
      key: 'uploadedBy',
      header: 'Uploaded by',
      headerClassName: `text-left text-neutral-400 text-xs font-normal px-4 py-2 bg-[#161717] whitespace-nowrap ${uploadedByColumnWidth}`,
      className: `px-4 py-2 ${uploadedByColumnWidth} align-middle`,
      accessor: (release: Release) => (
        <span
          className="block text-white text-sm group-hover:text-neutral-200 transition-colors truncate"
          title={release.uploadedByName || undefined}
        >
          {release.uploadedByName || '-'}
        </span>
      ),
    },
    ...(showActionsColumn
      ? [
          {
            key: 'actions',
            header: 'Actions',
            headerClassName:
              'text-right text-neutral-400 text-xs font-normal px-4 py-2 bg-[#161717] whitespace-nowrap w-[8%]',
            className: 'px-4 py-2 w-[8%] align-middle',
            accessor: (release: Release) =>
              release.source === 'Portal' ? (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onRequestDeleteRelease(release);
                    }}
                    className="inline-flex size-8 items-center justify-center rounded-md text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300"
                    title="Delete release"
                    aria-label={`Delete ${release.versionName || 'release'}`}
                  >
                    <DeleteIcon className="size-4 fill-current" />
                  </button>
                </div>
              ) : null,
          },
        ]
      : []),
  ];

  const releaseRowClassName = (release: Release, index: number) => {
    const isLegacy = release.source === 'Hubspot';
    return [
      index === currentReleases.length - 1 ? 'border-b-0' : '',
      isLegacy
        ? 'cursor-default'
        : 'hover:bg-neutral-800/50 hover:shadow-lg hover:z-10 cursor-pointer group',
    ]
      .filter(Boolean)
      .join(' ');
  };

  return (
    <div className="bg-[#161717] rounded-xl overflow-hidden flex flex-col flex-1">
      <div className="overflow-y-auto overflow-x-hidden flex-1 relative">
        <DataTable
          data={currentReleases}
          columns={releaseColumns}
          className="overflow-x-hidden"
          tableClassName="table-fixed"
          theadClassName="sticky top-0 bg-[#161717] z-10"
          headerRowClassName="border-b border-neutral-800"
          rowBaseClassName="border-b border-neutral-800 transition-all duration-200 relative"
          rowClassName={releaseRowClassName}
          onRowClick={onOpenRelease}
        />
      </div>
      {totalPages > 1 && (
        <div className="px-4 py-2 border-t border-neutral-800 flex items-center justify-between bg-[#161717]">
          <div className="text-sm text-neutral-400">
            Showing {startIndex + 1} to {Math.min(endIndex, releases.length)} of{' '}
            {releases.length} releases
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPageChange(Math.max(1, currentPage - 1));
              }}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm font-medium text-neutral-400 bg-neutral-800 rounded-md hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {getVisiblePages(currentPage, totalPages).map((page, index) => {
                if (page === '...') {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 text-neutral-500"
                    >
                      ...
                    </span>
                  );
                }

                return (
                  <button
                    type="button"
                    key={page}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPageChange(page);
                    }}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                      page === currentPage
                        ? 'text-white bg-teal-600'
                        : 'text-neutral-400 bg-neutral-800 hover:bg-neutral-700'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPageChange(Math.min(totalPages, currentPage + 1));
              }}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm font-medium text-neutral-400 bg-neutral-800 rounded-md hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getVisiblePages(currentPage: number, totalPages: number) {
  const pages: Array<number | '...'> = [];
  const maxPagesToShow = 5;

  if (totalPages <= maxPagesToShow) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      pages.push('...');
    }

    pages.push(totalPages);
  }

  return pages;
}
